import { useEffect, useState, useMemo } from "react";
import { FileMappingSuggestionDto } from "api/api.typing";
import { applyBatchToMappings, mergeSuggestions, NO_LANGUAGE_CODE, validateMappings } from "./fileMappingHelpers";

export interface FileMapping {
  sourceFileIds?: number[];
  languageCodes: string[];
}

export const useFileMappingState = (files: File[], fileMappingSuggestions: FileMappingSuggestionDto[]) => {
  /* ---------- State ---------- */
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [batchSourceFileIds, setBatchSourceFileIds] = useState<number[]>([]);
  const [batchLanguages, setBatchLanguages] = useState<string[]>([]);
  const [fileMappings, setFileMappings] = useState<Map<string, FileMapping>>(new Map());

  useEffect(() => {
    if (!fileMappingSuggestions.length) return;

    setFileMappings((prevMappings) => mergeSuggestions(prevMappings, fileMappingSuggestions));
  }, [fileMappingSuggestions]);

  /* ---------- Selection ---------- */
  const handleSelectAll = (checked: boolean) => {
    setSelectedFiles(checked ? new Set(files.map((f) => f.name)) : new Set());
  };

  const handleFileSelect = (fileName: string, checked: boolean) => {
    setSelectedFiles((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(fileName);
      } else {
        next.delete(fileName);
      }
      return next;
    });
  };

  /* ---------- Batch ---------- */
  const handleBatchSourceFilesChange = (values: number[]) => {
    setBatchSourceFileIds(values);
  };

  const handleBatchLanguagesChange = (values: string[]) => {
    setBatchLanguages(values.includes(NO_LANGUAGE_CODE) ? [NO_LANGUAGE_CODE] : values);
  };

  /* ---------- Per-file mapping ---------- */
  const handleSourceFileChange = (fileName: string, sourceFileIds: number[]) => {
    setFileMappings((prev) => {
      const next = new Map(prev);
      const existing = next.get(fileName) ?? { languageCodes: [] };
      next.set(fileName, { ...existing, sourceFileIds });
      return next;
    });
  };

  const handleLanguageChange = (fileName: string, languageCodes: string[]) => {
    setFileMappings((prev) => {
      const next = new Map(prev);
      const existing = next.get(fileName) ?? { languageCodes: [] };
      next.set(fileName, { ...existing, languageCodes });
      return next;
    });
  };

  /* ---------- Apply batch ---------- */
  const handleApplyToSelected = () => {
    setFileMappings((prev) => applyBatchToMappings(selectedFiles, prev, batchSourceFileIds, batchLanguages));
  };

  /* ---------- Validation ---------- */
  const areAllMappingsComplete = useMemo(() => validateMappings(files, fileMappings), [files, fileMappings]);

  return {
    // State
    selectedFiles,
    batchSourceFileIds,
    batchLanguages,
    fileMappings,
    areAllMappingsComplete,

    handleSelectAll,
    handleFileSelect,
    handleBatchSourceFilesChange,
    handleBatchLanguagesChange,
    handleSourceFileChange,
    handleLanguageChange,
    handleApplyToSelected,
  };
};
